import React from "react"
import { ApiPerformanceData } from "./types"
import { useSearch } from "./useSearch"

export type DurationOption = "5 minutes ago" | "30 minutes ago" | "1 hour ago" | "6 hours ago" | "12 hours ago" | "1 day ago" | "5 days ago"

interface TableProps {
  loading: boolean
  error: string | null
  data: ApiPerformanceData[]
  onRetry: () => void
  duration: string
  onDurationChange: (duration: string) => void
}

const DURATION_OPTIONS: { label: string; value: DurationOption }[] = [
  { label: "5m", value: "5 minutes ago" },
  { label: "30m", value: "30 minutes ago" },
  { label: "1h", value: "1 hour ago" },
  { label: "6h", value: "6 hours ago" },
  { label: "12h", value: "12 hours ago" },
  { label: "1d", value: "1 day ago" },
  { label: "5d", value: "5 days ago" }
]

const Table: React.FC<TableProps> = ({ loading, error, data, onRetry, duration, onDurationChange }) => {
  const { searchTerm, setSearchTerm, filteredData, clearSearch, hasActiveSearch } = useSearch(data)
  const formatValue = (value: number | null): string => {
    if (value === null || value === undefined) return "N/A"
    return value.toFixed(2)
  }

  const getUptimeClass = (uptime: number): string => {
    if (uptime >= 99.9) return "status-excellent"
    if (uptime >= 99) return "status-good"
    if (uptime >= 95) return "status-warning"
    return "status-critical"
  }

  const getErrorRateClass = (errorRate: number): string => {
    if (errorRate < 0.1) return "status-excellent"
    if (errorRate < 1) return "status-good"
    if (errorRate < 5) return "status-warning"
    return "status-critical"
  }

  const getResponseTimeClass = (responseTime: number): string => {
    if (responseTime < 0.5) return "status-excellent"
    if (responseTime < 1) return "status-good"
    if (responseTime < 2) return "status-warning"
    return "status-critical"
  }

  return (
    <div className='performance-table-container'>
      <div className='table-header'>
        <div className='header-left'>
          <h1>⚡ API Performance Metrics</h1>
          {hasActiveSearch && (
            <div className='search-results-info'>
              Found {filteredData.length} of {data.length} services
            </div>
          )}
        </div>
        <div className='header-right'>
          <div className='search-input-wrapper'>
            <span className='search-icon'>🔍</span>
            <input type='text' className='search-input' placeholder='Search...' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {hasActiveSearch && (
              <button className='clear-search-button' onClick={clearSearch} title='Clear search'>
                ✕
              </button>
            )}
          </div>
          <span className='data-count'>{!loading && !error && `${filteredData.length} services`}</span>
        </div>
      </div>

      {/* Duration Tabs */}
      <div className='duration-tabs-container'>
        <div className='duration-tabs'>
          {DURATION_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`duration-tab ${duration === option.value ? "active" : ""}`}
              onClick={() => onDurationChange(option.value)}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className='table-wrapper'>
        <table className='performance-table'>
          <thead>
            <tr>
              <th className='sticky-col'>
                <div className='th-content'>
                  <span>Product</span>
                  <span className='th-subtitle'>&nbsp;</span>
                </div>
              </th>
              <th>
                <div className='th-content'>
                  <span>Service</span>
                  <span className='th-subtitle'>&nbsp;</span>
                </div>
              </th>
              <th>
                <div className='th-content'>
                  <span>Region</span>
                  <span className='th-subtitle'>&nbsp;</span>
                </div>
              </th>
              <th>
                <div className='th-content'>
                  <span>Uptime</span>
                  <span className='th-subtitle'>(%)</span>
                </div>
              </th>
              <th>
                <div className='th-content'>
                  <span>Avg Response Time</span>
                  <span className='th-subtitle'>(seconds)</span>
                </div>
              </th>
              <th>
                <div className='th-content'>
                  <span>DB Response Time</span>
                  <span className='th-subtitle'>(seconds)</span>
                </div>
              </th>
              <th>
                <div className='th-content'>
                  <span>Error Rate</span>
                  <span className='th-subtitle'>(%)</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className='loading-cell'>
                  <div className='loading-container'>
                    <div className='spinner'></div>
                    <p>Loading performance metrics...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className='error-cell'>
                  <div className='error-container'>
                    <div className='error-icon'>⚠️</div>
                    <h3>Failed to load data</h3>
                    <p className='error-message'>{error}</p>
                    <button className='retry-button' onClick={onRetry}>
                      🔄 Retry
                    </button>
                  </div>
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={7} className='empty-cell'>
                  <div className='empty-container'>
                    <div className='empty-icon'>{hasActiveSearch ? "�" : "�📊"}</div>
                    <p>{hasActiveSearch ? `No results found for "${searchTerm}"` : "No performance data available"}</p>
                    {hasActiveSearch && (
                      <button className='retry-button' onClick={clearSearch}>
                        Clear Search
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredData.map((row, index) => (
                <tr key={index}>
                  <td className='sticky-col product-cell'>{row.product || "N/A"}</td>
                  <td className='service-cell'>{row.service || "N/A"}</td>
                  <td className='region-cell'>
                    <span className='region-badge'>{row.region || "N/A"}</span>
                  </td>
                  <td className={getUptimeClass(row.uptime)}>{formatValue(row.uptime)}</td>
                  <td className={getResponseTimeClass(row.avgResponseTime)}>{formatValue(row.avgResponseTime)}</td>
                  <td>{formatValue(row.avgDbResponseTime)}</td>
                  <td className={getErrorRateClass(row.errorRate)}>{formatValue(row.errorRate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Table
